import { useState, useEffect, useRef, useMemo } from "react";
import classNames from "classnames";
import { Divider, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DelIcon } from "../../utils/svg-components";
import { Button } from "../../components/button/button";
import CheckboxField from "../form-fields/checkbox-field/check-box";
import SelectField from "../form-fields/select-field/select-field";
import { useAuth } from "../../context/AuthContext";
import { FilterStyled } from "./filter.style";
import SaveFilterModal from "./save-filter-modal/save-filter-modal";
import { isSomething } from "../../utils/utils";

const FilterComponent = (props) => {
  const {
    state, // The default filter state (array)
    setState,
    options = [],
    isLoading,
    label = "Filter",
    onClearFilters,
    singleSelectKeys = [],
    disablePaddingBorder = true,
    className,
  } = props;

  // Local UI state
  const [toggleDrawer, setToggleDrawer] = useState(false);
  const [internalState, setInternalState] = useState(state);
  // contextSavedFilters holds custom filters for the current context.
  const [contextSavedFilters, setContextSavedFilters] = useState([]);
  // selectedFilter: if empty, the dropdown will show its placeholder.
  const [selectedFilter, setSelectedFilter] = useState("");
  // State to control the Save Filter Modal visibility.
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [loadedFilter, setLoadedFilter] = useState(state);
  const defaultState = useMemo(() => state, []);

  const filterRef = useRef(null);
  const { userDetails, updateUserSavedFilters, updateLastSelectedFilter } =
    useAuth();

  // Current context is derived from the label (in lowercase)
  const currentContext = label.toLowerCase();

  // When the drawer is closed, reset internal state.
  useEffect(() => {
    if (!toggleDrawer) {
      setInternalState(state);
    }
  }, [toggleDrawer, state]);

  // Close the drawer if clicking outside.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSaveModal) return; // If save modal is open, do nothing

      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setInternalState(state);
        setToggleDrawer(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [state, showSaveModal]);

  useEffect(() => {
    if (userDetails && userDetails.savedFilters) {
      const globalFilters = userDetails.savedFilters;
      const currentFilters = globalFilters[currentContext] || [];
      setContextSavedFilters(currentFilters);
      // Check if there's a last selected filter for this context.
      const lastSelected = userDetails.lastSelectedFilters?.[currentContext];
      if (lastSelected) {
        setSelectedFilter(lastSelected);
        const saved = currentFilters.find((f) => f.name === lastSelected);
        if (saved && saved.configurations.length > 0) {
          const latestConfig =
            saved.configurations[saved.configurations.length - 1].filters;
          setState(latestConfig);
          setInternalState(latestConfig);
        }
      } else {
        // Only set default if nothing is selected
        if (!selectedFilter) {
          setSelectedFilter("Default");
          setState(state);
          setInternalState(state);
        }
      }
    }
  }, [userDetails, currentContext]); // Removed state and setState from dependencies

  // Save a new custom filter. Accepts a filter name as a parameter.
  const saveFilter = async (filterName) => {
    if (!filterName.trim()) return;
    if (filterName.trim().toLowerCase() === "default") {
      alert('The name "Default" is reserved.');
      return;
    }
    // Check if a filter with the same name already exists.
    const duplicateExists = contextSavedFilters.some(
      (f) => f.name.toLowerCase() === filterName.toLowerCase()
    );

    if (duplicateExists) {
      // Show an error message to the user.
      alert(
        "A filter with this name already exists. Please choose a different name."
      );
      return;
    }

    // Build the minimal configuration (include titleName and color)
    const newConfig = internalState.map(({ key, value, titleName, color }) => ({
      key,
      value,
      titleName,
      color,
    }));
    const configObject = { filters: newConfig };

    const existingIndex = contextSavedFilters.findIndex(
      (f) => f.name.toLowerCase() === filterName.trim().toLowerCase()
    );

    let updatedContextFilters;
    if (existingIndex >= 0) {
      const existingFilter = contextSavedFilters[existingIndex];
      updatedContextFilters = [...contextSavedFilters];
      updatedContextFilters[existingIndex] = {
        ...existingFilter,
        configurations: [...existingFilter.configurations, configObject],
      };
    } else {
      const newFilter = {
        name: filterName.trim(),
        configurations: [configObject],
      };
      updatedContextFilters = [...contextSavedFilters, newFilter];
    }

    // Merge with other contexts.
    const globalFilters = userDetails?.savedFilters || {};
    const mergedFilters = {
      ...globalFilters,
      [currentContext]: updatedContextFilters,
    };

    setContextSavedFilters(updatedContextFilters);
    await updateUserSavedFilters(mergedFilters);
    setSelectedFilter(filterName.trim());
    await updateLastSelectedFilter(currentContext, filterName.trim());
  };

  // Load a saved filter configuration.
  const loadFilter = async (name) => {
    if (name === "Default") {
      setState(defaultState);
      setInternalState(defaultState);
      setLoadedFilter(defaultState);
      setSelectedFilter("Default");
      await updateLastSelectedFilter(currentContext, "Default");
    } else {
      const saved = contextSavedFilters.find((f) => f.name === name);
      if (saved && saved.configurations.length > 0) {
        const latestConfig =
          saved.configurations[saved.configurations.length - 1].filters;
        setState(latestConfig);
        setInternalState(latestConfig);
        setLoadedFilter(latestConfig);
        setSelectedFilter(name);
        await updateLastSelectedFilter(currentContext, name);
      }
    }
  };

  // Delete a custom saved filter.
  const deleteFilter = async (name) => {
    if (name.trim().toLowerCase() === "default") return;
    const updatedContextFilters = contextSavedFilters.filter(
      (f) => f.name !== name
    );
    const globalFilters = userDetails?.savedFilters || {};
    const mergedFilters = {
      ...globalFilters,
      [currentContext]: updatedContextFilters,
    };
    setContextSavedFilters(updatedContextFilters);
    await updateUserSavedFilters(mergedFilters);
    if (selectedFilter === name) {
      // Reset to default.
      setSelectedFilter("Default");
      setState(state);
      setInternalState(state);
      await updateLastSelectedFilter(currentContext, "Default");
    }
  };

  // Remove All Filters (this simply clears the UI filters by calling onClearFilters)
  const removeAllFilters = () => {
    setInternalState([]);
    setState([]);
    onClearFilters && onClearFilters();
    setSelectedFilter("");
    // Note: We are not updating Firestore for "Remove All Filters"
  };

  const onApply = () => {
    // Apply the current filter changes.
    setState(internalState);
    // Do not update loadedFilter here so that isDirty remains true if changes were made.
    setToggleDrawer(false);
  };

  // Handle checkbox changes (single-select logic).
  const handleCheckboxChange = (x, option, isChecked) => {
    if (singleSelectKeys.includes(x.key)) {
      setInternalState((prev) => [
        ...prev.filter((s) => s.key !== x.key),
        { key: x.key, value: option.value, color: x.color, titleName: x.name },
      ]);
    } else {
      if (isChecked) {
        setInternalState((prev) =>
          prev.filter((s) => !(s.key === x.key && s.value === option.value))
        );
      } else {
        setInternalState((prev) => [
          ...prev,
          {
            key: x.key,
            value: option.value,
            color: x.color,
            titleName: x.name,
          },
        ]);
      }
    }
  };

  // Prepare the dropdown options: always include a "Default" option.
  const defaultOption = { label: "Default", value: "Default" };
  const dropdownOptions = [
    defaultOption,
    ...contextSavedFilters.map((f) => ({
      label: f.name,
      value: f.name,
    })),
  ];

  useEffect(() => {
    const filtersMatchDefault =
      JSON.stringify(internalState) === JSON.stringify(defaultState);

    // If filters match default and selectedFilter is not "Default", force it
    if (filtersMatchDefault && selectedFilter !== "Default") {
      setSelectedFilter("Default");
    }

    // If filters don't match default and user hasn't selected a named filter, clear it
    if (!filtersMatchDefault && selectedFilter === "Default") {
      setSelectedFilter("");
    }
  }, [internalState, defaultState, selectedFilter]);

  const isDirty =
    JSON.stringify(internalState) !== JSON.stringify(loadedFilter);
  // console.log(className);

  return (
    <div className={classNames({ "class-check": true })}>
      <FilterStyled
        className={classNames({
          "no-padding-border": disablePaddingBorder,
          override: true,
        })}
        ref={filterRef}
      >
        <div className="filter-head">
          <div className="left-controls">
            <div className="btn">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FilterListIcon />}
                sx={{ width: "max-content" }}
                onClick={() => setToggleDrawer((prev) => !prev)}
              >
                {label}
              </Button>
              <SelectField
                name="savedFilter"
                options={dropdownOptions}
                value={selectedFilter}
                onChange={(e) => loadFilter(e.target.value)}
                placeholder="Select a filter"
              />
            </div>
            <div className="selections">
              {internalState?.map((option, i) => {
                if (option === "all") return null;
                const filterOption = options?.find(
                  (opt) => opt.key === option.key
                );
                const selectedOption = filterOption?.options?.find(
                  (opt) => opt.value === option.value
                );
                const labelText =
                  selectedOption?.label || option.value || "Unknown";
                return (
                  <Button
                    key={`${option.key}-${option.value}-${i}`}
                    variant="outlined"
                    color="secondary"
                    endIcon={<DelIcon />}
                    sx={{ background: option?.color, width: "max-content" }}
                    onClick={() => {
                      setState((prev) =>
                        prev?.filter(
                          (s) =>
                            !(
                              s?.key === option?.key &&
                              s?.value === option?.value
                            )
                        )
                      );
                      setInternalState((prev) =>
                        prev?.filter(
                          (s) =>
                            !(
                              s?.key === option?.key &&
                              s?.value === option?.value
                            )
                        )
                      );
                    }}
                  >
                    {`${option?.titleName}: ${labelText}`}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="right-controls">
            {isDirty && (
              <Button
                variant="filled"
                color={"tertiary"}
                size="medium"
                onClick={() => setShowSaveModal(true)}
                sx={{ width: "110px" }}
              >
                Save Filter
              </Button>
            )}
            {selectedFilter && selectedFilter.toLowerCase() !== "default" && (
              <Button
                variant={"filled"}
                color={"error"}
                sx={{ width: "110px" }}
                onClick={() => deleteFilter(selectedFilter)}
              >
                Delete Filter
              </Button>
            )}
            <Button
              variant="link"
              onClick={removeAllFilters}
              sx={{ width: "max-content" }}
            >
              Remove All Filters
            </Button>
          </div>
        </div>

        {/* Filter drawer */}
        <div className={classNames({ drawer: true, visible: toggleDrawer })}>
          <div className="filter-lists">
            {options?.map((x, index) => {
              const isAllSelected =
                internalState?.filter((s) => s?.key === x?.key)?.length ===
                x?.options?.length;
              return (
                <div className="filter" key={`${x.key}-category`}>
                  <Typography className="title">{x?.name}</Typography>
                  <div
                    className={classNames({
                      options: true,
                      "hide-scrollbar": true,
                      "border-apply": index > 0,
                    })}
                  >
                    {x?.isSelectAll && (
                      <div className="option-flex">
                        <CheckboxField
                          size="small"
                          value={!!isAllSelected}
                          in
                          indeterminate={
                            internalState?.filter((s) => s?.key === x?.key)
                              ?.length > 0 && !isAllSelected
                          }
                          onChange={() => {
                            if (isAllSelected) {
                              setInternalState((prev) =>
                                prev.filter((s) => s.key !== x.key)
                              );
                            } else {
                              const newSelections = x.options.map((option) => ({
                                key: x.key,
                                value: option.value,
                                color: x.color,
                                titleName: x.name,
                              }));
                              setInternalState((prev) => [
                                ...prev.filter((s) => s.key !== x.key),
                                ...newSelections,
                              ]);
                            }
                          }}
                        />
                        <div className="sub-title">Select All</div>
                      </div>
                    )}
                    {x?.options?.map((option) => {
                      const isChecked = internalState?.find(
                        (s) => s?.key === x?.key && s?.value === option?.value
                      );
                      const labelText =
                        option?.label || option?.value || "Unknown";
                      return (
                        <div
                          className="option-flex"
                          key={`${option.value}-${x.key}-option`}
                        >
                          <CheckboxField
                            size="small"
                            value={!!isChecked}
                            onChange={() =>
                              handleCheckboxChange(x, option, !!isChecked)
                            }
                          />
                          <div className="sub-title">{labelText}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <Divider orientation="horizontal" className="divider" />
          <div className="apply">
            <Button
              onClick={() => setToggleDrawer(false)}
              variant="outlined"
              color="secondary"
              sx={{ width: "110px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={onApply}
              variant="contained"
              color="primary"
              sx={{ width: "110px" }}
            >
              Apply
            </Button>
          </div>
        </div>

        {/* Save Filter Modal */}
        {showSaveModal && (
          <SaveFilterModal
            show={showSaveModal}
            onClose={() => setShowSaveModal(false)}
            onSave={(name) => {
              saveFilter(name);
              onApply();
              setShowSaveModal(false);
            }}
            filters={internalState} // Passing current filters to be displayed in the modal
          />
        )}
      </FilterStyled>
    </div>
  );
};

export default FilterComponent;
